// Workspace Service API Client

import { BaseApiClient } from '../BaseApiClient';
import {
  GetMeResponse,
  GetPricingModelResponse,
  GetPricingModelsResponse,
  GetUserResponse,
  GetUsersParams,
  GetUsersResponse,
  GetWorkspaceInfoResponse,
  GetWorkspacesByAuthProfileResponse,
  PricingModelType,
  SkillLevel,
  UpdatePricingModelRequest,
  UpdatePricingModelResponse,
  UpdateSkillLevelsRequest,
  UpdateUserRequest,
  UpdateUserResponse,
  UpdateWorkspaceRequest,
  UpdateWorkspaceResponse,
} from './types';

class WorkspaceApiClient extends BaseApiClient {
  constructor() {
    super({ prefix: 'workspace' });
  }

  // === WORKSPACE MANAGEMENT ===

  /**
   * Get current workspace information
   */
  async getWorkspaceInfo(): Promise<GetWorkspaceInfoResponse> {
    return this.makeRequest<GetWorkspaceInfoResponse>('/info');
  }

  /**
   * Update workspace information and settings
   */
  async updateWorkspaceInfo(
    request: UpdateWorkspaceRequest
  ): Promise<UpdateWorkspaceResponse> {
    return this.makeRequest<UpdateWorkspaceResponse>('/info', {
      method: 'PATCH',
      data: request,
    });
  }

  /**
   * Get workspaces by auth profile
   */
  async getWorkspacesByAuthProfile(): Promise<GetWorkspacesByAuthProfileResponse> {
    return this.makeRequest<GetWorkspacesByAuthProfileResponse>(
      '/by-auth-profile'
    );
  }

  // === USER MANAGEMENT ===

  /**
   * Get current user profile information
   */
  async getMe(): Promise<GetMeResponse> {
    return this.makeRequest<GetMeResponse>('/users/me');
  }

  /**
   * Get user profile by ID
   */
  async getUser(id: string): Promise<GetUserResponse> {
    return this.makeRequest<GetUserResponse>(`/users/${id}`);
  }

  /**
   * Update user profile
   */
  async updateUser(
    id: string,
    request: UpdateUserRequest
  ): Promise<UpdateUserResponse> {
    return this.makeRequest<UpdateUserResponse>(`/users/${id}`, {
      method: 'PATCH',
      data: request,
    });
  }

  /**
   * Get all workspace users with optional filtering
   */
  async getUsers(params?: GetUsersParams): Promise<GetUsersResponse> {
    return this.makeRequest<GetUsersResponse>('/users/', {
      params: params,
    });
  }

  // === SKILL LEVELS ===

  /**
   * Get all skill levels for the workspace
   */
  async getSkillLevels(): Promise<SkillLevel[]> {
    return this.makeRequest<SkillLevel[]>('/levels/');
  }

  /**
   * Update skill levels for the workspace
   */
  async updateSkillLevels(
    request: UpdateSkillLevelsRequest
  ): Promise<SkillLevel[]> {
    return this.makeRequest<SkillLevel[]>('/levels/', {
      method: 'PATCH',
      data: request,
    });
  }

  // === PRICING MODELS ===

  /**
   * Get all pricing models for the workspace
   */
  async getPricingModels(): Promise<GetPricingModelsResponse> {
    return this.makeRequest<GetPricingModelsResponse>('/pricing-models/');
  }

  /**
   * Get specific pricing model by type
   */
  async getPricingModel(
    type: PricingModelType
  ): Promise<GetPricingModelResponse> {
    return this.makeRequest<GetPricingModelResponse>(`/pricing-models/${type}`);
  }

  /**
   * Create or update a pricing model
   */
  async updatePricingModel(
    type: PricingModelType,
    request: UpdatePricingModelRequest
  ): Promise<UpdatePricingModelResponse> {
    return this.makeRequest<UpdatePricingModelResponse>(
      `/pricing-models/${type}`,
      {
        method: 'PUT',
        data: request,
      }
    );
  }
}

// Export singleton instance
export const workspaceApi = new WorkspaceApiClient();
export default workspaceApi;
